import classnames from 'classnames'
import { CSSProperties } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { IProductCard } from '@src/components/product-card/product-card.types'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { round } from '@src/utils/functions/utils'

import useScrollAnimation, { Options } from '../../hooks/useScrollAnimation'
import { RichText, RichTextProps } from '../../molecules'
import Button, { ButtonProps } from '../button'
import { Image, ImageProps } from '../image'
import Swiper from '../swiper'
import { NAV_POSITION } from '../swiper/Swiper'

import styles from './featureSectionSeven.module.scss'

export interface FeatureSectionSevenProps {
  richTextPropsList: RichTextProps[]
  buttonPropsList: ButtonProps[]
  imagePropsList: ImageProps[]
  productCards: IProductCard[]
  className?: string
  style?: CSSProperties
  layoutType: LAYOUT_TYPES
}

const animationOptions: Options = {
  completeOffsetPercentage: 0.3,
  startOffsetPercentage: 0.3,
  showOverflow: false,
  degreeOfRotation: 45,
}

const addAnimation = (element: HTMLElement, percentagePassed: number) => {
  element.style.transform = `rotate(${round(percentagePassed * animationOptions.degreeOfRotation, 3)}deg)`
}

export const FeatureSectionSeven = ({
  richTextPropsList,
  buttonPropsList,
  imagePropsList,
  productCards,
  className = '',
  style,
  layoutType,
}: FeatureSectionSevenProps) => {
  const mainImageProps = imagePropsList[0]
  const mainButtonProps = buttonPropsList[0]
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const maxWidth = getMaxWidthInPx({
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const { wrapperRef, imageRef } = useScrollAnimation<HTMLDivElement, HTMLDivElement>(addAnimation, animationOptions)

  return (
    <div style={style} className={classnames(className, styles.wrapper, styles[`wrapper__${layoutType}`])}>
      <div className={styles.imageAndContentWrapper}>
        {mainImageProps?.url && (
          <div
            className={classnames({
              [styles.imageWrapper ?? '']: animationOptions.showOverflow,
            })}
            ref={wrapperRef}
          >
            <Image
              alt={mainImageProps?.alt}
              {...mainImageProps}
              ref={imageRef}
              className={classnames(mainImageProps.className, styles.image, styles.image_animation)}
            />
          </div>
        )}
        <div className={styles.textAndButtonWrapper}>
          <div className={styles.textWrapper}>
            {richTextPropsList.map((richTextProps, idx) => (
              <RichText key={idx} {...richTextProps} />
            ))}
          </div>
          {mainButtonProps && (
            <Button {...mainButtonProps} className={classnames(mainButtonProps.className, styles.button)} />
          )}
        </div>
      </div>
      <Swiper
        id="feature-section-seven-carousal"
        className={styles.productList}
        numberOfVisibleSlides={isDesktop ? 4 : 1}
        variableWidth
        showNavigator={isDesktop}
        navPosition={NAV_POSITION.RIGHT_WITH_PADDING}
        withChildWrapper
      >
        {productCards.map(productCard => {
          return (
            <ProductCard
              key={`${productCard?.product?.productId}_${productCard?.product?.cardVariants?.[0]?.displayConfig.value}`}
              className={styles.productCard}
              shouldShowMore
              optionLimit={4}
              productCard={productCard}
              maxWidth={maxWidth}
            />
          )
        })}
      </Swiper>
    </div>
  )
}

export default FeatureSectionSeven
